import React from 'react';

import { Box, InputAdornment, OutlinedInput, Typography, debounce } from '@mui/material';

import { useConversationsContext } from '../../../contexts/ConversationsContext';
import { ConversationItem, OfferStatus, VenueListConversationsFilter } from '../../../graphql/API';
import { useListOfferQuery } from '../../../hooks/reactQuery/useOfferQuery';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useAppSelector } from '../../../redux/hooks';
import Icons from '../../../themes/icons';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import Button from '../../Button/Button';
import { SelectOptionV2 } from '../../Formiks/SelectionOptionV2/SelectionOptionV2';
import { OptionsBar } from '../../OptionsBar/OptionsBar';
import { NoChatFound } from '../NoChatFound';
import ChatThreadSkeleton from './Skeleton';
import { Thread } from './Thread';

type Props = {
  selectedConversation: ConversationItem | null;
  onConversationSelectionHandle: (conversation: ConversationItem | null) => void;
};

const ConversationsList: React.FC<{
  activeTab: number;
  isFetching: boolean;
  isLoading: boolean;
  data: (ConversationItem | null | undefined)[];
  lastElementRef: (node: HTMLDivElement | null) => void;
  selectedConversation: ConversationItem | null;
  onConversationSelectionHandle: (conversation: ConversationItem) => void;
  venueConversationFilter: Partial<VenueListConversationsFilter> | undefined;
  setVenueConversationFilter:
    | React.Dispatch<React.SetStateAction<Partial<VenueListConversationsFilter>>>
    | undefined;
  setConversations: React.Dispatch<
    React.SetStateAction<(ConversationItem | null | undefined)[] | undefined>
  >;
}> = ({
  isFetching,
  isLoading,
  data,
  activeTab,
  lastElementRef,
  selectedConversation,
  onConversationSelectionHandle,
  venueConversationFilter,
  setVenueConversationFilter,
  setConversations,
}) => {
  const [localNameSorting, setLocalNameSorting] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    if (venueConversationFilter?.sortBy === 'messageTime') {
      setLocalNameSorting(undefined);
    }
  }, [venueConversationFilter]);

  const handleSorting = (name: string, nextSortbyRecent: boolean) => {
    if (name === 'username') {
      setConversations((prev) => {
        const sorted = [...(prev || [])].sort((a, b) => {
          const fullNameA = `${a?.firstname} ${a?.lastname}`.toLowerCase();
          const fullNameB = `${b?.firstname} ${b?.lastname}`.toLowerCase();
          return fullNameA.localeCompare(fullNameB);
        });

        return localNameSorting ? sorted.reverse() : sorted;
      });
      setLocalNameSorting((prev) => !prev);
    } else {
      if (setVenueConversationFilter) {
        setVenueConversationFilter((prev) => ({
          ...prev,
          sortBy: name,
          sortbyRecent: nextSortbyRecent,
        }));
      }
    }
  };

  return (
    <Box
      sx={{
        paddingY: '10px',
        overflow: 'auto',
        height: 'calc(-240px + 100vh)',
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid #F1F2F1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Button
          variant='text'
          sx={{ p: 0, color: '#848484', fontSize: '13px', marginLeft: 10 }}
          onClick={() =>
            handleSorting(
              'username',
              venueConversationFilter?.sortBy === 'username' ? !localNameSorting : true,
            )
          }
        >
          Name{' '}
          <Icons.ExpandMoreIcon
            style={{
              rotate: localNameSorting ? '180deg' : '0deg',
            }}
          />
        </Button>

        <Button
          variant='text'
          sx={{ p: 0, color: '#848484', fontSize: '13px', marginRight: 2 }}
          onClick={() =>
            handleSorting(
              'messageTime',
              venueConversationFilter?.sortBy === 'messageTime'
                ? !venueConversationFilter?.sortbyRecent
                : true,
            )
          }
        >
          Time{' '}
          <Icons.ExpandMoreIcon
            style={{
              rotate:
                venueConversationFilter?.sortBy === 'messageTime' &&
                venueConversationFilter?.sortbyRecent
                  ? '180deg'
                  : '0deg',
            }}
          />
        </Button>
      </Box>
      {!isFetching && data?.length === 0 && (
        <NoChatFound
          title={activeTab === 0 ? 'There are no active chats' : 'There are no closed chats'}
        />
      )}
      {data &&
        data.map(
          (conversation, index) =>
            conversation && (
              <div
                {...(data.length === index + 1 && {
                  ref: lastElementRef,
                })}
              >
                <Thread
                  active={conversation.conversationId === selectedConversation?.conversationId}
                  {...conversation}
                  onConversation={() => {
                    onConversationSelectionHandle(conversation);
                  }}
                />
              </div>
            ),
        )}
      {isLoading || isFetching ? (
        <>
          <ChatThreadSkeleton />
          <ChatThreadSkeleton />
          <ChatThreadSkeleton />
          <ChatThreadSkeleton />
          <ChatThreadSkeleton />
        </>
      ) : null}
    </Box>
  );
};

const TAB_OPTIONS = [
  { label: 'Open', value: 'open' },
  { label: 'Closed', value: 'closed' },
];

export const ChatThreads: React.FC<Props> = ({
  selectedConversation,
  onConversationSelectionHandle,
}) => {
  const venueState = useAppSelector((state) => state.venue.value);

  const isOnlineBrand = React.useMemo(
    () => venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online),
    [venueState],
  );

  const { data: offers, isFetching: isOffersFetching } = useListOfferQuery({
    status: OfferStatus.active,
  });

  const {
    venueConversationFilter,
    data,
    isLoading = false,
    isFetching = false,
    hasNextPage = false,
    fetchNextPage,
    revalidateConversations,
    setVenueConversationFilter,
  } = useConversationsContext() || {};

  const [conversations, setConversations] = React.useState<
    (ConversationItem | null | undefined)[] | undefined
  >(data);

  const { lastElementRef } = useInfiniteScroll({
    hasMore: hasNextPage,
    isFetching: isFetching || isLoading,
    cb() {
      if (fetchNextPage) fetchNextPage();
    },
  });

  React.useEffect(() => {
    setConversations(data);
  }, [data]);

  React.useEffect(() => {
    if (revalidateConversations) {
      revalidateConversations();
    }
  }, []);

  const handleTabChange = (val: string) => {
    setVenueConversationFilter &&
      setVenueConversationFilter((prev) => ({ ...prev, closedChats: val === 'closed' }));
    onConversationSelectionHandle(null);
  };

  const handleSearch = debounce((value) => {
    const searchTerm = value.trim().toLowerCase();

    if (!searchTerm) {
      setConversations(data); // Reset to original data if input is empty
      return;
    }

    const filtered = data?.filter((conversation) => {
      const first = conversation?.firstname?.toLowerCase() || '';
      const last = conversation?.lastname?.toLowerCase() || '';
      return first.includes(searchTerm) || last.includes(searchTerm);
    });

    setConversations(filtered);
  }, 500);

  const handleChange = React.useCallback(
    (value?: string) => {
      handleSearch(value);
    },
    [handleSearch],
  );

  const handleOfferChange = (value: string) => {
    if (setVenueConversationFilter)
      setVenueConversationFilter((prev) => ({
        ...prev,
        searchByOfferTitle: value === 'all' ? '' : value,
      }));
  };

  const offersOptions = React.useMemo(() => {
    const _offers = offers.map((offer) => ({
      label: String(offer.title),
      value: String(offer.title),
    }));
    return [{ label: isOnlineBrand ? 'All products' : 'All offers', value: 'all' }, ..._offers];
  }, [offers, isOnlineBrand]);

  return (
    <>
      <Box>
        <Box sx={{ paddingX: '20px', paddingY: '10px' }}>
          <OutlinedInput
            fullWidth
            id='outlined-adornment-amount'
            placeholder='Search a user...'
            startAdornment={
              <InputAdornment position='start'>
                <Icons.SearchIcon />
              </InputAdornment>
            }
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            size='small'
            sx={{
              fontFamily: 'Poppins',
              fontSize: '15px',
              borderRadius: '8px',
              boxShadow: 'none',
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px',
                borderColor: '#F1F2F1', // Default
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px',
                borderColor: '#F1F2F1', // Hover
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#F1F2F1', // Focus (uses theme primary color)
                borderWidth: '1px',
              },
              '& input::placeholder': {
                color: '#757575',
                fontSize: '15px',
                opacity: 1,
              },
            }}
          />
          <Box mt={'8px'} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
            <SelectOptionV2
              name='products'
              id='slt-products'
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '6px',
                    border: '1px solid #E6E6E6',
                    boxShadow: '0px 0px 40px 0px rgba(0, 0, 0, 0.10)',
                    '& .MuiMenuItem-root': {
                      fontSize: '14px',
                      fontFamily: 'Poppins',
                      padding: '12px 16px',
                      '&:not(:last-child)': {
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                      },
                    },
                  },
                },
              }}
              sx={{
                color: venueConversationFilter?.searchByOfferTitle ? '#111' : '#757575',
                fontSize: '15px',
                fontFamily: 'Poppins',
                padding: '1px 0',
                minWidth: '216px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                  borderColor: '#F1F2F1', // Default
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                  borderColor: '#F1F2F1', // Hover
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#F1F2F1', // Focus (uses theme primary color)
                  borderWidth: '1px',
                },
              }}
              options={offersOptions}
              size='small'
              disabled={isOffersFetching}
              value={
                !venueConversationFilter?.searchByOfferTitle
                  ? 'all'
                  : venueConversationFilter?.searchByOfferTitle
              }
              onChange={(e) => {
                handleOfferChange(e.target.value as string);
              }}
            />
            <OptionsBar
              options={TAB_OPTIONS}
              value={
                venueConversationFilter?.closedChats === true
                  ? 'closed'
                  : venueConversationFilter?.closedChats === false
                    ? 'open'
                    : 'open'
              }
              onChange={(value) => {
                if (value) handleTabChange(value);
              }}
            />
          </Box>
        </Box>
        <ConversationsList
          isFetching={isFetching}
          isLoading={isLoading}
          activeTab={venueConversationFilter?.closedChats ? 1 : 0}
          data={conversations || []}
          lastElementRef={lastElementRef}
          selectedConversation={selectedConversation}
          onConversationSelectionHandle={onConversationSelectionHandle}
          venueConversationFilter={venueConversationFilter}
          setVenueConversationFilter={setVenueConversationFilter}
          setConversations={setConversations}
        />
        {/* <TabPanelContainer
          centered
          defaultValue={activeTab}
          handleTabChange={handleTabChange}
          tabSx={{
            m: 0,
            minWidth: '174px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center !important',
          }}
          panelSx={{ p: 0 }}
          panels={[
            {
              label: 'Active',
              children: (
                <ConversationsList
                  isFetching={isFetching}
                  isLoading={isLoading}
                  activeTab={activeTab}
                  data={data || []}
                  lastElementRef={lastElementRef}
                  selectedConversation={selectedConversation}
                  onConversationSelectionHandle={onConversationSelectionHandle}
                />
              ),
            },
            {
              label: 'Closed',
              children: (
                <ConversationsList
                  isFetching={isFetching}
                  isLoading={isLoading}
                  activeTab={activeTab}
                  data={data || []}
                  lastElementRef={lastElementRef}
                  selectedConversation={selectedConversation}
                  onConversationSelectionHandle={onConversationSelectionHandle}
                />
              ),
            },
          ]}
        /> */}
      </Box>
    </>
  );
};
